import './About.css';
import { useEffect } from 'react';
import ProfilePhoto from './IMAGE/KALAW.jpeg';

function About({ onNavigateHome }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about-page">
      <div className="about-hero-strip">
        <div className="about-strip-inner">
          <p className="about-strip-label">Personal Profile</p>
          <h1 className="about-strip-name">About Me</h1>
          <p className="about-strip-role">Exploring the intersection of hardware and software engineering.</p>
          <div className="hero-stats-row">
            <span className="hero-stat-pill">▸ Batangas, Philippines</span>
            <span className="hero-stat-pill">▸ Computer Engineering</span>
            <span className="hero-stat-pill">▸ Aspiring Developer</span>
          </div>
          <div className="hero-watermark">◇</div>
        </div>
      </div>

      <main className="about-main page-wrapper">
        <aside className="about-sidebar sidebar">
          <div className="sidebar-photo-wrap">
            <img src={ProfilePhoto} alt="Christian D. Kalaw" className="sidebar-photo" />
            <div className="photo-caption">Christian D. Kalaw</div>
          </div>

          <div className="sidebar-card">
            <p className="sidebar-card-title">Details</p>
            <ul className="detail-list">
              <li className="detail-row">
                <span className="detail-key">Location</span>
                <span className="detail-val">Philippines</span>
              </li>
              <li className="detail-row">
                <span className="detail-key">Field</span>
                <span className="detail-val">Computer Engineering</span>
              </li>
              <li className="detail-row">
                <span className="detail-key">Instrument</span>
                <span className="detail-val">Guitar</span>
              </li>
              <li className="detail-row">
                <span className="detail-key">Status</span>
                <span className="detail-val open">Open to opportunities</span>
              </li>
            </ul>
          </div>

          <div className="sidebar-card">
            <p className="sidebar-card-title">What Drives Me</p>
            <div className="drives-tags">
              <span className="drives-tag">Innovator</span>
              <span className="drives-tag">Goal-Oriented</span>
              <span className="drives-tag">Lifelong Learner</span>
              <span className="drives-tag">Team Player</span>
            </div>
          </div>
        </aside>

        <section className="about-content main-content">
          <section>
            <h2 className="section-title">Who I Am</h2>
            <div className="section-divider"></div>
            <p className="bio-text">
              I'm <strong>Christian D. Kalaw</strong>, a Computer Engineering student with a deep-rooted passion for understanding how technology works from the ground up. I chose this field because I've always been fascinated by the magic that happens at the intersection of hardware and software — how lines of code can breathe life into physical circuits.
            </p>
            <p className="bio-text">
              My core strength lies in my adaptability and problem-solving mindset. Whether I'm designing embedded systems, architecting databases, or writing full-stack web applications, I approach every challenge as an opportunity to innovate. I thrive in environments that require both logical precision and creative thinking.
            </p>
            <p className="bio-text">
              Looking forward, my goal is to become a versatile <strong>Software Developer</strong> who builds scalable, high-impact solutions. I'm particularly interested in roles where I can leverage my understanding of low-level systems to write highly efficient, optimized software that makes a tangible difference in people's lives.
            </p>
          </section>

          <div className="stats-row">
            <div className="stat-cell">
              <div className="stat-num">4x</div>
              <div className="stat-key">Academic Honors</div>
            </div>
            <div className="stat-cell">
              <div className="stat-num">3rd</div>
              <div className="stat-key">Year Eng.</div>
            </div>
            <div className="stat-cell">
              <div className="stat-num">10+</div>
              <div className="stat-key">Core Skills</div>
            </div>
            <div className="stat-cell">
              <div className="stat-num">100%</div>
              <div className="stat-key">Dedication</div>
            </div>
          </div>

          <section>
            <p className="section-label">My Strengths</p>
            <h2 className="section-title">My Strengths</h2>
            <div className="section-divider"></div>
            <div className="strengths-grid">
              <div className="strength-card">
                <div className="strength-icon">🧠</div>
                <h3 className="strength-name">Adaptability</h3>
                <p className="strength-desc">Quick to learn and integrate new technologies, frameworks, and methodologies into my workflow regardless of the domain.</p>
              </div>
              <div className="strength-card">
                <div className="strength-icon">🧩</div>
                <h3 className="strength-name">Problem Solving</h3>
                <p className="strength-desc">A highly analytical approach to breaking down complex technical challenges into manageable, logical, and efficient solutions.</p>
              </div>
              <div className="strength-card">
                <div className="strength-icon">📈</div>
                <h3 className="strength-name">Continuous Growth</h3>
                <p className="strength-desc">Dedicated to lifelong learning, constantly refining my skills, and staying ahead of the curve in a fast-paced industry.</p>
              </div>
              <div className="strength-card">
                <div className="strength-icon">🤝</div>
                <h3 className="strength-name">Communication</h3>
                <p className="strength-desc">Articulating complex technical concepts clearly to diverse teams, ensuring smooth collaboration from concept to deployment.</p>
              </div>
            </div>
          </section>

          <section>
            <p className="section-label">Outside the Code</p>
            <h2 className="section-title">Outside the Code</h2>
            <div className="section-divider"></div>
            <p className="bio-text">
              My interests outside engineering shape how I think, communicate, and create.
            </p>

            <div className="interests-grid">
              <div className="interest-item">
                <div className="interest-num">01</div>
                <div className="interest-body">
                  <div className="interest-name">Online Gaming</div>
                  <div className="interest-desc">
                    Competitive gaming sharpens strategic thinking and quick decisions.
                  </div>
                  <div className="interest-tags">
                    <span className="interest-tag">Strategy</span>
                    <span className="interest-tag">Competitive</span>
                  </div>
                </div>
              </div>

              <div className="interest-item">
                <div className="interest-num">02</div>
                <div className="interest-body">
                  <div className="interest-name">Travelling</div>
                  <div className="interest-desc">
                    Every destination teaches something new about people and systems.
                  </div>
                  <div className="interest-tags">
                    <span className="interest-tag">Culture</span>
                    <span className="interest-tag">Adventure</span>
                  </div>
                </div>
              </div>

              <div className="interest-item">
                <div className="interest-num">03</div>
                <div className="interest-body">
                  <div className="interest-name">Playing with Pets</div>
                  <div className="interest-desc">
                    Resets the mind. Teaches patience and staying present.
                  </div>
                  <div className="interest-tags">
                    <span className="interest-tag">Wellness</span>
                    <span className="interest-tag">Joy</span>
                  </div>
                </div>
              </div>

              <div className="interest-item">
                <div className="interest-num">04</div>
                <div className="interest-body">
                  <div className="interest-name">Playing Guitar</div>
                  <div className="interest-desc">
                    Music is my creative counterbalance. Learning pieces trains patience and precision.
                  </div>
                  <div className="interest-tags">
                    <span className="interest-tag">Music</span>
                    <span className="interest-tag">Creative</span>
                  </div>
                </div>
              </div>

              <div className="interest-item">
                <div className="interest-num">05</div>
                <div className="interest-body">
                  <div className="interest-name">Exploring New Things</div>
                  <div className="interest-desc">
                    Actively seeking unfamiliar tools and fields to step outside the comfort zone.
                  </div>
                  <div className="interest-tags">
                    <span className="interest-tag">Curiosity</span>
                    <span className="interest-tag">Growth</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="quote-block">
            <p className="quote-text">"The people who are crazy enough to think they can change the world are the ones who do."</p>
            <p className="quote-author">— Steve Jobs</p>
          </div>

          <div className="cta-row">
            <button className="btn-primary" onClick={onNavigateHome}>
              View My Work
            </button>
            <a href="https://www.linkedin.com/in/christian-kalaw-590b29357/" target="_blank" rel="noreferrer" className="btn-secondary">
              LinkedIn
            </a>
            <a href="mailto:christiankalaw@gmail.com" className="btn-secondary">
              Get In Touch
            </a>
          </div>
        </section>
      </main>
      <footer className="about-footer">
        <p>Christian D. Kalaw &copy; {new Date().getFullYear()}</p>
        <button className="footer-back" onClick={onNavigateHome}>Back to Portfolio</button>
      </footer>
    </div>
  );
}

export default About;
